/**
 * The invoice list (plan §4, spec §G2). Redrawn on the list shell in Wave 15D
 * (C14 §10.6 items 1–2, ADR-0033; T-6.16): search, status / client / year
 * chips, sortable columns, paging and Export, and a row opens the invoice.
 *
 * **Drafts first, because a draft is the only row that needs somebody.** An
 * issued invoice is a record; a draft is a piece of unfinished work, and a firm
 * opening this screen is nearly always coming back to one. The server already
 * answers in that order (drafts, then the newest issued), and the list keeps
 * it until somebody sorts a column.
 *
 * One read serves the page. A list that fetched its client per row is the N+1
 * that makes a billing screen feel slow at exactly the moment a firm is trying
 * to get its invoicing done, so the client name is resolved once from a single
 * client read and joined here.
 *
 * **Search and the chips work on what was read** — the newest 200, the most
 * the invoices API gives in one read (C14 §10.6 item 8: no server paging in
 * this change). A firm with more is told so in the description.
 */

import { Alert, Badge, Button } from '@fiveox/ui'
import { FileText, Plus, Repeat } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ExportButton,
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  RecordSection,
  StatusBadge,
  statusTone,
  useListTable,
} from '../../components/page'
import { INVOICE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type BillingVocabulary,
  type CaseChangeAdjustment,
  type Invoice,
  billing,
  iso,
  rupees,
} from '../../lib/api/billing'
import { NewInvoiceDialog } from './NewInvoiceDialog'
import {
  Money,
  activeClients,
  clientFilter,
  invoiceStatus,
  namesOf,
  yearFilter,
} from './shared'

/** The most the invoices API returns in one read (`le=200`). */
const INVOICE_PAGE = 200

/** An invoice as the list shows it: the client's name joined, and the status
 *  the badge and the chip read. */
export interface InvoiceRow extends Invoice {
  client_name: string
  /** `overdue` for an issued invoice still owed past its due date, otherwise
   *  the server's own `payment_state`. */
  shown_status: string
}

/** The status chip. *Unpaid* is the old Unpaid tab — the server's own
 *  `unpaid` filter: issued, and less received than the total. */
export const STATUS_OPTIONS: Array<{ value: string; label: string; match: (row: InvoiceRow) => boolean }> = [
  { value: 'draft', label: 'Drafts', match: (row) => row.status === 'draft' },
  {
    value: 'owing',
    label: 'Unpaid or part paid',
    match: (row) =>
      row.status === 'issued' && (row.payment_state === 'unpaid' || row.payment_state === 'part_paid'),
  },
  { value: 'overdue', label: 'Overdue', match: (row) => row.shown_status === 'overdue' },
  { value: 'paid', label: 'Paid', match: (row) => row.payment_state === 'paid' },
  { value: 'cancelled', label: 'Cancelled', match: (row) => row.status === 'cancelled' },
]

export function Invoices() {
  const { can } = useEvaluation()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [names, setNames] = useState<Record<string, string>>({})
  const [vocab, setVocab] = useState<BillingVocabulary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [changes, setChanges] = useState<CaseChangeAdjustment[]>([])
  const [adjusting, setAdjusting] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [list, book] = await Promise.all([
        billing.invoices({ limit: INVOICE_PAGE }),
        activeClients(),
      ])
      setInvoices(list.data)
      const counted = list.meta?.total
      setTotal(typeof counted === 'number' ? counted : null)
      setNames(namesOf(book))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The invoices could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void billing
      .vocabulary()
      .then((r) => setVocab(r.data))
      .catch(() => setVocab(null))
  }, [])

  // w5a, T-3.11 — the re-pricing an approved plan change owes. Read once, and
  // a failure is silent: this is a queue beside the work, not the work, and a
  // billing screen that refuses to render because one extra read 500'd would be
  // worse than a missing card.
  useEffect(() => {
    void billing
      .caseChanges()
      .then((r) => setChanges(r.data))
      .catch(() => setChanges([]))
  }, [])

  // T-3.11. Only the dearer half is a button here: it raises a **draft**, which
  // is reversible by discarding it. The credit-note half is done on the invoice
  // screen, behind step-up and the credit approver, because it is not.
  const adjust = async (change: CaseChangeAdjustment) => {
    setAdjusting(change.id)
    setError(null)
    try {
      await billing.recordAdjustment(change.id, {})
      const pending = await billing.caseChanges()
      setChanges(pending.data)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That re-pricing could not be recorded.')
    } finally {
      setAdjusting(null)
    }
  }

  const today = iso(new Date())
  const rows = useMemo<InvoiceRow[]>(
    () =>
      invoices.map((invoice) => ({
        ...invoice,
        client_name: names[invoice.client_id] ?? '—',
        shown_status: invoiceStatus(invoice, today),
      })),
    [invoices, names, today],
  )

  const columns = useMemo<ListColumn<InvoiceRow>[]>(
    () => [
      {
        key: 'number',
        label: 'Invoice',
        width: 170,
        required: true,
        phone: true,
        sortField: 'number',
        // A draft has no number, and saying "Draft" is more use than an empty
        // cell or a made-up placeholder that looks like a real series number.
        render: (row) => (
          <span className="block min-w-0">
            <span className="whitespace-nowrap">{row.number ?? 'Draft'}</span>
            {/* On a phone the client column is gone; its name rides here. */}
            <span className="text-muted-foreground block max-w-[13rem] truncate text-xs font-normal sm:hidden">
              {row.client_name}
            </span>
            {row.origin === 'action' && (
              <span className="text-muted-foreground block text-xs font-normal">
                Raised by the case engine
              </span>
            )}
          </span>
        ),
        exportValue: (row) => row.number ?? 'Draft',
      },
      {
        key: 'client',
        label: 'Client',
        width: 200,
        sortField: 'client_name',
        // 13rem, not `xs`: with the rail beside it a 1280 screen is ~990px of
        // table, and at `xs` the Status column went under the scroll edge.
        render: (row) => <span className="block max-w-[10rem] truncate sm:max-w-[13rem]">{row.client_name}</span>,
        exportValue: (row) => row.client_name,
      },
      {
        key: 'issue_date',
        label: 'Date',
        width: 100,
        sortField: 'issue_date',
        render: (row) => <span className="text-muted-foreground whitespace-nowrap">{row.issue_date ?? '—'}</span>,
        exportValue: (row) => row.issue_date ?? '',
      },
      {
        key: 'due_date',
        label: 'Due',
        width: 100,
        sortField: 'due_date',
        render: (row) => <span className="text-muted-foreground whitespace-nowrap">{row.due_date ?? '—'}</span>,
        exportValue: (row) => row.due_date ?? '',
      },
      {
        key: 'grand_total',
        label: 'Total',
        width: 110,
        align: 'right',
        sortField: 'grand_total',
        render: (row) => <Money value={row.grand_total} />,
        exportValue: (row) => row.grand_total,
      },
      {
        key: 'outstanding',
        label: 'Outstanding',
        width: 110,
        align: 'right',
        sortField: 'outstanding',
        render: (row) => (row.status === 'issued' ? <Money value={row.outstanding} /> : '—'),
        exportValue: (row) => (row.status === 'issued' ? row.outstanding : ''),
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        phone: true,
        render: (row) => <StatusBadge kind="invoice" status={row.shown_status} />,
        exportValue: (row) => statusTone('invoice', row.shown_status).label,
      },
    ],
    [],
  )

  const list = useListTable(rows, {
    columns,
    search: (row) => [row.number ?? 'Draft', row.client_name],
    filters: [
      {
        key: 'status',
        label: 'Status',
        allLabel: 'Any status',
        options: STATUS_OPTIONS.map(({ value, label }) => ({ value, label })),
        match: (row, value) => STATUS_OPTIONS.find((o) => o.value === value)?.match(row) ?? true,
      },
      clientFilter(rows, names),
      yearFilter(rows),
    ],
  })

  // Formatted server-side values, added up only for a heading. Every figure a
  // firm acts on comes from the server — this one is a count and a sum for
  // orientation, and it is the one place a `Number` is acceptable because
  // nothing is written back from it.
  const outstanding = invoices
    .filter((row) => row.status === 'issued')
    .reduce((sum, row) => sum + Number(row.outstanding), 0)
    .toFixed(2)
  const drafts = invoices.filter((row) => row.status === 'draft').length

  const canCreate = can(INVOICE, 'can_create')
  const newInvoice = canCreate ? (
    <Button onClick={() => setCreating(true)}>
      <Plus size={16} aria-hidden /> New invoice
    </Button>
  ) : undefined

  const description = loading
    ? 'Your invoices, drafts first.'
    : `${invoices.length} ${invoices.length === 1 ? 'invoice' : 'invoices'}${
        drafts ? ` · ${drafts} ${drafts === 1 ? 'draft' : 'drafts'} to finish` : ''
      } · ${rupees(outstanding)} outstanding${
        total !== null && total > invoices.length
          ? `. Showing your latest ${invoices.length} of ${total}.`
          : ''
      }`

  return (
    <div className="space-y-6">
      <ListPage
        title="Invoices"
        description={description}
        primary={newInvoice}
        loading={loading}
        error={error}
        isEmpty={list.isEmpty}
        empty={{
          title: 'No invoices yet',
          description:
            'An invoice starts as a draft — raise one here, or let the case engine raise one when a stage says so. Nothing is numbered until you issue it.',
          action: newInvoice,
        }}
        toolbar={
          <ListToolbar
            list={list}
            placeholder="Search by number or client"
            actions={
              <ExportButton rows={list.sorted} columns={columns} filename="prodesk-invoices" />
            }
          />
        }
      >
        <ListTable
          list={list}
          rowKey={(row) => row.id}
          rowHref={(row) => `/billing/invoices/${row.id}`}
        />
      </ListPage>

      {changes.length > 0 && (
        <RecordSection
          id="repricing"
          icon={<Repeat size={18} aria-hidden />}
          title="Re-pricing owed"
          description="Plan changes a partner has approved, where the difference has not yet reached the client's ledger."
        >
          <ul className="divide-border divide-y text-sm">
            {changes.map((change) => (
              <li key={change.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0 last:pb-0">
                <span className="font-medium">{change.case_number ?? 'A case'}</span>
                <span className="text-muted-foreground">
                  {change.from_amount && change.to_amount
                    ? `${rupees(change.from_amount)} → ${rupees(change.to_amount)}`
                    : 'plan changed'}
                </span>
                {change.owes === 'invoice' && change.delta && (
                  <Badge tone="warning">{rupees(change.delta)} to bill</Badge>
                )}
                {change.owes === 'credit_note' && change.delta && (
                  <Badge tone="info">{rupees(change.delta.replace('-', ''))} to credit</Badge>
                )}
                {change.owes === 'manual' && <Badge tone="neutral">work it out by hand</Badge>}
                {change.owes === 'invoice' && canCreate && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto"
                    onClick={() => void adjust(change)}
                    loading={adjusting === change.id}
                    loadingText="Recording…"
                    disabled={adjusting !== null && adjusting !== change.id}
                  >
                    Raise the difference
                  </Button>
                )}
              </li>
            ))}
          </ul>
          <p className="text-muted-foreground mt-4 text-xs">
            {/* Why the two halves are not both one button: one of them is a
                draft somebody then issues, and the other is a credit note,
                which cancels part of a tax invoice and is as irreversible as
                issuing one. They do not belong behind the same click. */}
            A dearer plan raises a draft for the difference, which somebody then issues. A cheaper
            one is credited against the invoice that charged the old plan — open that invoice and
            credit it, because a credit note cannot be undone.
          </p>
        </RecordSection>
      )}

      {vocab && !vocab.can_issue && (
        <Alert tone="info">
          <FileText size={16} aria-hidden /> You can prepare invoices, but issuing one needs a{' '}
          {vocab.issue_approver_role}. Your firm chooses who approves money actions in Settings.
        </Alert>
      )}

      {creating && (
        <NewInvoiceDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            void load()
          }}
        />
      )}
    </div>
  )
}
